import { Injectable } from '@angular/core';
import {
  Dossier,
  findCorrespondingServiceType,
  Fitter,
} from '@sc/data-access-models';
import { AppointmentUtils, dayjs } from '@sc/utils';
import {
  DossierCustomMoveInfos,
  DossierEventExtendedProps,
  EventExtendedProps,
  GenericEventExtendedProps,
  ResourceType,
  ResourceTypeEnum,
} from '../models';

@Injectable({ providedIn: 'root' })
export class ResourcesManagerService {
  private static readonly defaultTechnicianName = '__default__tech__';
  private static readonly defaultInterventionVehicleName =
    '__default__intervention__vehicle';
  private static readonly defaultRVName = '__default__rv__';

  constructDefaultTechResourceId(fitterId: string) {
    return this.constructResourceId(
      fitterId,
      'TECHNICIAN',
      ResourcesManagerService.defaultTechnicianName
    );
  }

  constructDefaultInterventionVehicleId(fitterId: string) {
    return this.constructResourceId(
      fitterId,
      'INTERVENTION_VEHICLE',
      ResourcesManagerService.defaultInterventionVehicleName
    );
  }

  constructDefaultRVResourceId(fitterId: string) {
    return this.constructResourceId(
      fitterId,
      'RV_DETAILED',
      ResourcesManagerService.defaultRVName
    );
  }

  constructResourceId(
    fitterId: string,
    type: ResourceType,
    specificResName?: string
  ) {
    if (
      type !== 'TECHNICIAN' &&
      type !== 'INTERVENTION_VEHICLE' &&
      type !== 'RV_DETAILED' &&
      !!specificResName
    ) {
      console.warn(
        'TRYING TO BUILD A CALENDAR COLUMN ID THAT IS NOT A TECHNICIAN COLUMN NOR AN INTERVENTION VEHICLE COLUMN NOR A RV COLUMN BUT HAS A SPECIFIC NAME :',
        type,
        specificResName
      );
    }
    return `${fitterId}_${type}${specificResName ? `$${specificResName}` : ''}`;
  }

  parseResourceId(resId: string) {
    if (!resId) {
      return {};
    }

    const [headResId, specificResName] = resId.split('$');

    let resType: ResourceType | undefined;
    for (const testedResType of Object.values(ResourceTypeEnum)) {
      if (headResId.includes(testedResType)) {
        resType = testedResType;
      }
    }

    if (!resType) {
      return {};
    }

    const fitterId = headResId.replace(`_${resType}`, '');

    return {
      fitterId,
      resType,
      techName: resType === 'TECHNICIAN' ? specificResName : undefined,
      interventionVehicleName:
        resType === 'INTERVENTION_VEHICLE' ? specificResName : undefined,
      rvName: resType === 'RV_DETAILED' ? specificResName : undefined,
    };
  }

  getResourceFitterId(resId: string) {
    const parsedResId = this.parseResourceId(resId);
    return parsedResId.fitterId;
  }

  getResourceType(resId: string) {
    const parsedResId = this.parseResourceId(resId);
    return parsedResId.resType;
  }

  getResourceTechnician(resId: string) {
    const parsedResId = this.parseResourceId(resId);
    return parsedResId.techName;
  }

  isDefaultTechnician(techNameOrResId: string) {
    return techNameOrResId.includes(
      ResourcesManagerService.defaultTechnicianName
    );
  }

  getResourceInterventionVehicle(resId: string) {
    const parsedResId = this.parseResourceId(resId);
    return parsedResId.interventionVehicleName;
  }

  isDefaultInterventionVehicle(interventionVehicleNameOrResId: string) {
    return interventionVehicleNameOrResId.includes(
      ResourcesManagerService.defaultInterventionVehicleName
    );
  }

  getResourceRV(resId: string) {
    const parsedResId = this.parseResourceId(resId);
    return parsedResId.rvName;
  }

  isDefaultRV(rvNameOrResId: string) {
    return rvNameOrResId.includes(ResourcesManagerService.defaultRVName);
  }

  /**
   * Tells if the resource is of 'center' type, whatever the chosen display parameter is (detailed/condensed)
   */
  isAnyCenterRes(resType: ResourceType): resType is 'IN_CENTER' | 'TECHNICIAN' {
    return resType === 'IN_CENTER' || resType === 'TECHNICIAN';
  }

  /**
   * Tells if the resource is of 'replacement vehicle' type, whatever the chosen display parameter is (detailed/condensed)
   */
  isAnyRVRes(
    resType: ResourceType
  ): resType is 'REPLACEMENTVEHICLE' | 'RV_DETAILED' {
    return resType === 'REPLACEMENTVEHICLE' || resType === 'RV_DETAILED';
  }

  /**
   * Tells if the resource is of 'outside' type, whatever the chosen display parameter is (detailed/condensed)
   */
  isAnyOutsideRes(
    resType: ResourceType
  ): resType is 'OUTSIDE' | 'INTERVENTION_VEHICLE' {
    return resType === 'OUTSIDE' || resType === 'INTERVENTION_VEHICLE';
  }

  isDossierEventExtendedProps(
    extendedProps: EventExtendedProps
  ): extendedProps is DossierEventExtendedProps {
    return 'dossier' in extendedProps;
  }

  isGenericEventExtendedProps(
    extendedProps: EventExtendedProps
  ): extendedProps is GenericEventExtendedProps {
    return 'event' in extendedProps;
  }

  /**
   * Takes a dossier and the informations about the move/resize event and builds the resulting dossier with it's attributes adapted
   * It does not check if the move/resize is possible but the resulting dossier should be used to check this
   */
  generateDossierAfterMove(
    dossierBeforeMove: Dossier,
    dossierMoveInfos: DossierCustomMoveInfos,
    fitters: Fitter[]
  ): Dossier {
    /**
     * HOURS resize and drag and drop
     */
    const diffTime = dayjs.duration(
      dayjs(dossierMoveInfos.endTime).diff(dayjs(dossierMoveInfos.time))
    );
    const diffMinute = diffTime.asMinutes();
    const dossierResult: Dossier = {
      ...dossierBeforeMove,
      appointment: {
        ...dossierBeforeMove.appointment,
        timezone: dossierMoveInfos.timezone,
        assignedResources: [
          ...(dossierBeforeMove.appointment?.assignedResources || []),
        ],
      },
      canNotifyCustomer: false,
    };

    const isEventMoved =
      dossierMoveInfos.newResource && dossierMoveInfos.oldResource;
    const isEventResized = !!dossierMoveInfos.resizeResource;

    const isRVDragged =
      (isEventMoved && this.isAnyRVRes(dossierMoveInfos.oldResource.type)) ||
      (isEventResized && this.isAnyRVRes(dossierMoveInfos.resizeResource.type));

    // If the event dragged is one for replacement vehicles, then we should first update the vehicle use period in the dossier
    if (isRVDragged) {
      const rvPeriodSameAsApmtPeriod =
        dayjs(dossierMoveInfos?.time)
          .tz(dossierMoveInfos.timezone)
          .isSame(
            dossierBeforeMove.appointment?.assignedReplacementVehicleUse
              ?.start || dossierBeforeMove.appointment?.time,
            'minutes'
          ) &&
        dayjs(dossierMoveInfos?.endTime)
          .tz(dossierMoveInfos.timezone)
          .isSame(
            dossierBeforeMove.appointment?.assignedReplacementVehicleUse?.end ||
              dossierBeforeMove.appointment?.endTime ||
              dossierBeforeMove.appointment?.time,
            'minutes'
          );

      if (rvPeriodSameAsApmtPeriod) {
        dossierResult.appointment.assignedReplacementVehicleUse = null;
      } else {
        dossierResult.appointment.assignedReplacementVehicleUse = {
          start: dossierMoveInfos.time,
          end: dossierMoveInfos.endTime,
        };
      }
    }

    const newFitter = fitters.find(
      (fitter) =>
        dossierMoveInfos.newResource?.fitterId === fitter.id ||
        dossierMoveInfos.resizeResource?.fitterId === fitter.id
    );

    // To update the period of the appointment, we need to clear the following condition :
    // The event is not representing the replacement vehicle (center / outside)
    if (!isRVDragged) {
      dossierResult.appointment.time = dossierMoveInfos.time;
      dossierResult.appointment.endTime = dossierMoveInfos.endTime;
      dossierResult.appointment.interventionDuration = diffMinute;
      dossierResult.appointment.id = AppointmentUtils.generateNewAppointmentId(
        newFitter,
        dossierResult.appointment.time,
        dossierResult.appointment.endTime
      );
    }

    if (isEventMoved) {
      /**
       * Old and new resources (columns) are specified so we are in drag & drop
       */

      const isSameColumnDrag =
        dossierMoveInfos.newResource.resId ===
        dossierMoveInfos.oldResource.resId;
      const isSameFitterDrag =
        dossierMoveInfos.newResource.fitterId ===
        dossierMoveInfos.oldResource.fitterId;

      // Check all the changes regarding the old and new column types
      if (!isSameColumnDrag) {
        if (
          this.isAnyOutsideRes(dossierMoveInfos.oldResource.type) &&
          this.isAnyCenterRes(dossierMoveInfos.newResource.type)
        ) {
          // If we leave an outside intervention column to a non-outside column
          dossierResult.appointment.interventionLocation = null;
          dossierResult.appointment.assignedInterventionVehicle = null;
          dossierResult.appointment.isConveying = false;
          dossierResult.appointment.outsideIntervention = false;
        } else if (
          this.isAnyCenterRes(dossierMoveInfos.oldResource.type) &&
          this.isAnyOutsideRes(dossierMoveInfos.newResource.type)
        ) {
          // If we leave a non-outside intervention column to an outside column
          dossierResult.appointment.interventionLocation = null;
          dossierResult.appointment.isConveying = false;
          dossierResult.appointment.outsideIntervention = true;
          dossierResult.appointment.isReplacementVehicleSelected = false;
          dossierResult.appointment.assignedReplacementVehicle = null;
        }

        // If we leave a technician column for another, then remove this technician from the dossier
        if (
          dossierMoveInfos.oldResource.type === 'TECHNICIAN' &&
          dossierMoveInfos.newResource.type === 'TECHNICIAN'
        ) {
          const oldTechName = this.getResourceTechnician(
            dossierMoveInfos.oldResource.resId
          );
          dossierResult.appointment.assignedResources =
            dossierResult.appointment.assignedResources?.filter(
              (tech) => tech !== oldTechName
            ) || [];
        }
        // If we drop into a technician column, then add this technician to the dossier
        if (dossierMoveInfos.newResource.type === 'TECHNICIAN') {
          const newTechName = this.getResourceTechnician(
            dossierMoveInfos.newResource.resId
          );
          if (this.isDefaultTechnician(newTechName)) {
            dossierResult.appointment.assignedResources = [];
          } else if (
            !dossierResult.appointment.assignedResources?.includes(newTechName)
          ) {
            dossierResult.appointment.assignedResources?.push(newTechName);
          }
        }

        // If we leave an interventionVehicle column, then remove this interventionVehicle from the dossier
        if (dossierMoveInfos.oldResource.type === 'INTERVENTION_VEHICLE') {
          dossierResult.appointment.assignedInterventionVehicle = null;
        }
        // If we drop into an interventionVehicle column, then add this interventionVehicle to the dossier
        if (dossierMoveInfos.newResource.type === 'INTERVENTION_VEHICLE') {
          const newInterventionVehicleName =
            this.getResourceInterventionVehicle(
              dossierMoveInfos.newResource.resId
            );
          if (!this.isDefaultInterventionVehicle(newInterventionVehicleName)) {
            dossierResult.appointment.assignedInterventionVehicle =
              newInterventionVehicleName;
          }
        }

        // If we leave a RV column, then remove this RV from the dossier
        if (dossierMoveInfos.oldResource.type === 'RV_DETAILED') {
          dossierResult.appointment.assignedReplacementVehicle = null;
        }
        // If we drop into a RV column, then add this RV to the dossier
        if (dossierMoveInfos.newResource.type === 'RV_DETAILED') {
          const newRVName = this.getResourceRV(
            dossierMoveInfos.newResource.resId
          );
          const foundRV = newFitter?.vehicles?.find(
            (v) => v.id + '##' + v.name === newRVName
          );

          if (!this.isDefaultRV(newRVName) && foundRV) {
            dossierResult.appointment.assignedReplacementVehicle = foundRV;
          }
        }
      }

      // Check all the changes regarding the old and new fitters
      if (!isSameFitterDrag && newFitter) {
        dossierResult.appointment.fitterId = newFitter.id;

        // Check if all technicians are present in the new fitter, if not remove the old ones
        const newFitterTechList =
          newFitter.resources
            ?.filter((res) => res.active)
            ?.map((res) => res.name) || [];
        dossierResult.appointment.assignedResources =
          dossierResult.appointment.assignedResources?.filter((tech) =>
            newFitterTechList.includes(tech)
          ) || [];

        // Check if the current center has the same interventionVehicle, if not remove it
        if (
          !!dossierResult.appointment.assignedInterventionVehicle &&
          newFitter.interventionVehicles
            ?.filter((vehicle) => vehicle.active)
            ?.findIndex(
              (vehicle) =>
                vehicle.name ===
                dossierResult.appointment.assignedInterventionVehicle
            ) === -1
        )
          dossierResult.appointment.assignedInterventionVehicle = null;

        // Check if current center has the same vehicle, if not remove the old ones
        if (dossierResult.appointment.assignedReplacementVehicle) {
          const hasLicensePlate =
            dossierResult.appointment.assignedReplacementVehicle
              .licensePlateNumber;
          const vehicle = newFitter?.vehicles?.find(
            (vehicle) =>
              vehicle.active &&
              ((hasLicensePlate &&
                dossierResult.appointment.assignedReplacementVehicle
                  ?.licensePlateNumber === vehicle.licensePlateNumber) ||
                (!hasLicensePlate &&
                  !!vehicle.licensePlateNumber &&
                  dossierResult.appointment.assignedReplacementVehicle?.id +
                    '##' +
                    dossierResult.appointment.assignedReplacementVehicle
                      ?.name ===
                    vehicle.id + '##' + vehicle.name))
          );

          dossierResult.appointment.assignedReplacementVehicle =
            vehicle || null;
        }

        // Check if current center has the same kind of intervention and select it if it has
        const newServiceType = findCorrespondingServiceType(
          {
            id: dossierResult?.appointment?.interventionId,
            operationType: dossierResult.claim.operationType,
            glassType: dossierResult.claim.glass.glass,
            isMobility: dossierResult.appointment.outsideIntervention,
            replacementVehicle: dossierResult.appointment.replacementVehicle,
            active: true,
            calibration: dossierResult.claim.calibration === 'true',
          },
          newFitter.serviceTypes
        );

        if (newServiceType?.id) {
          dossierResult.appointment.interventionId = newServiceType.id;
        }
      }
    } else {
      // Here we are dealing with a resize event
    }

    return dossierResult;
  }
}
